import {
  Button,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import Delete from "@material-ui/icons/Delete";
import Warning from "@material-ui/icons/Warning";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import DeviceSearchAutocomplete from "../../Components/DeviceSearchAutocomplete";

const CLOSED_SHIPMENT_STATUSES = ["delivered", "cancelled"];

export const isDeviceStockItem = (item) =>
  Boolean(item?.DeviceProfileId || item?.DeviceProfile?.id);

export const getDeviceStockItemId = (device) =>
  device?.StockItemId || device?.StockItem?.id;

export const getDeviceAllocationsForStockItem = (shipmentData, stockItem) => {
  const stockItemId = stockItem?.StockItemId || stockItem?.id;
  if (!stockItemId) {
    return [];
  }
  return (shipmentData?.Devices || []).filter(
    (device) =>
      getDeviceStockItemId(device)?.toString() === stockItemId.toString(),
  );
};

export default function DeviceAllocation({
  stockItem,
  shipmentData,
  setShipmentData,
  editable,
}) {
  const { t } = useTranslation();
  const [addingSerial, setAddingSerial] = useState();
  const allocations = shipmentData?.Devices || [];
  const isClosedShipment = CLOSED_SHIPMENT_STATUSES.includes(
    shipmentData?.status,
  );

  if (
    !shipmentData ||
    !stockItem ||
    shipmentData.shipmentDirection !== "between" ||
    !isDeviceStockItem(stockItem)
  ) {
    return null;
  }

  const stockItemId = stockItem?.StockItemId || stockItem?.id;
  const hasStockItemId =
    stockItemId !== undefined &&
    stockItemId !== null &&
    stockItemId !== "" &&
    Number.isInteger(Number(stockItemId));
  const itemAllocations = getDeviceAllocationsForStockItem(
    shipmentData,
    stockItem,
  );
  const missingCount = Math.max(
    Number(stockItem.count || 0) - itemAllocations.length,
    0,
  );

  const setAllocations = (Devices) => {
    setShipmentData({
      ...shipmentData,
      Devices,
    });
  };

  const removeAllocation = (device) => {
    setAllocations(allocations.filter((item) => item.id !== device.id));
  };

  const allocatedIds = allocations.map((item) => item.id);

  return (
    <Grid container spacing={1} alignItems="center">
      {itemAllocations.map((device) => (
        <AllocatedDeviceLine
          key={device.id || device.serialNumber}
          device={device}
          editable={editable && !isClosedShipment}
          onRemove={removeAllocation}
        />
      ))}
      {hasStockItemId && !isClosedShipment ? (
        Array.from({ length: missingCount }).map((_, index) => {
          const allocationSlotKey = `${stockItemId}-${index}`;
          return (
            <Grid item xs={12} key={allocationSlotKey}>
              {editable && addingSerial === allocationSlotKey ? (
                <DeviceSearchAutocomplete
                  fetchDevicesOnFocus={true}
                  excludeIds={allocatedIds}
                  additionalDataFields={[
                    "DeviceProfile.name",
                    "chassisNumber",
                    "color",
                  ]}
                  searchRestrictions={{
                    StockItemId: stockItemId,
                    ShipmentId: null,
                    ...(shipmentData.pickupAddress
                      ? { lcLocation: shipmentData.pickupAddress }
                      : {}),
                  }}
                  onChange={(e, value) => {
                    const deviceStockItemId = getDeviceStockItemId(value);
                    if (
                      !value ||
                      deviceStockItemId?.toString() !==
                        stockItemId.toString() ||
                      allocations.find((item) => item.id === value.id) ||
                      allocatedIds.includes(value.id)
                    ) {
                      return;
                    }
                    setAllocations([...allocations, value]);
                    setAddingSerial();
                  }}
                />
              ) : (
                <Tooltip
                  title={
                    <Typography style={{ fontSize: 15 }}>
                      {t(
                        "shipments.deviceSerialRequired",
                        "Device serial number needs to be assigned for this item",
                      )}
                    </Typography>
                  }
                >
                  <Button
                    startIcon={<Warning style={{ color: "yellow" }} />}
                    size="small"
                    variant="outlined"
                    disabled={!editable}
                    onClick={() => {
                      setAddingSerial(allocationSlotKey);
                    }}
                  >
                    {t("shipments.assignDevice", "Assign device")}
                  </Button>
                </Tooltip>
              )}
            </Grid>
          );
        })
      ) : missingCount && !isClosedShipment ? (
        <Grid item xs={12}>
          <Typography>
            {t(
              "shipments.stockItemReferenceRequired",
              "Stock item reference is required before allocating serials.",
            )}
          </Typography>
        </Grid>
      ) : null}
    </Grid>
  );
}

const AllocatedDeviceLine = ({ device, editable, onRemove }) => {
  return (
    <Grid item xs={12} container alignItems="center">
      <Grid item>
        {device.id ? (
          <Link href={`/cpanel/devices/${device.id}`}>
            {device.serialNumber}
          </Link>
        ) : (
          <Typography>{device.serialNumber}</Typography>
        )}
      </Grid>
      {editable ? (
        <Grid item>
          <IconButton
            size="small"
            onClick={() => {
              onRemove(device);
            }}
          >
            <Delete />
          </IconButton>
        </Grid>
      ) : null}
    </Grid>
  );
};
